import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { CopyOutlined, ShareAltOutlined, DownloadOutlined, DeleteOutlined, EllipsisOutlined, EditOutlined } from '@ant-design/icons';
import FolderCard from '../FolderCard/FolderCard';
import CardDownloadOverlay from '../CardDownloadOverlay/CardDownloadOverlay';
import { ReactComponent as ImagePlaceholder } from '../../images/placeholder-img.svg';
import { getStatusLabel, getStatusColor } from '../../utils/statusHelpers';
import { getEntryOrganizationLabel } from '../../utils/projectFilterSortHelpers';
import {
  canEditEntryInCardMenu,
  canShowDeleteInCardMenu,
  canShowDownloadInCardMenu,
  normalizeCardMenuDividers,
} from '../../utils/entryCardMenuPermissions';
import { filterActiveShareMembers } from '../../utils/userPresence';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './ProjectItemCard.module.scss';

function isEntryProject(entry: any): boolean {
  return entry?.entryType === 'Project' || String(entry?.entryType) === 'Project';
}

export type ProjectItemCardMenuAction = 'rename' | 'changeCover' | 'moveTo' | 'duplicate' | 'share' | 'download' | 'delete';

export type ProjectItemCardMoveDndConfig = {
  onMoveDragStart: (e: React.DragEvent, item: any) => void;
  onMoveDragEnd: (e: React.DragEvent) => void;
  onMoveFolderDragOver: (e: React.DragEvent, folderItem: any) => void;
  onMoveFolderDragLeave: (e: React.DragEvent, folderItem: any) => void;
  onMoveFolderDrop: (e: React.DragEvent, folderItem: any) => void;
  onMoveFileDragOver: (e: React.DragEvent, fileItem: any) => void;
  onMoveFileDragLeave: (e: React.DragEvent, fileItem: any) => void;
  onMoveFileDrop: (e: React.DragEvent, fileItem: any) => void;
  isActiveFolderDropTarget: boolean;
  isActiveFileDropTarget: boolean;
};

const ICON_SIZE = 18;

/** Geçici: çift tıklama ile rename kapalı. Tekrar açmak için `true` yapın. */
const DOUBLE_CLICK_RENAME_ENABLED = false;

interface ProjectItemCardProps {
  item: any;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  onNavigate: (item: any) => void;
  formatDate: (dateString: string) => string;
  selectable?: boolean;
  variant?: 'internal' | 'external';
  onMenuAction?: (action: ProjectItemCardMenuAction, item: any) => void;
  editingItemId?: number | null;
  onRenameSubmit?: (item: any, newName: string) => void;
  onRenameCancel?: () => void;
  /** Aynı seviye kardeşler arasında klasöre sürükleyerek taşıma (null = kapalı) */
  entryMoveDnd?: ProjectItemCardMoveDndConfig | null;
  /** false: "Move to" / "Duplicate" menüde yok, DnD zaten entryMoveDnd ile kapatılır (Admin, SuperAdmin, Editor) */
  allowMoveToAndDuplicate?: boolean;
  /** 0-100: indirme sırasında kart üstü overlay */
  downloadProgress?: number;
  /** Sürüklenen kart (kaynak) */
  isMoveDragSource?: boolean;
  /** Aktif sürükleme modu — dosya versiyonu veya klasöre taşıma */
  moveDragMode?: 'file' | 'folder' | null;
  /** Dosya→dosya merge: kaynak tek versiyonluysa true */
  canMergeToFile?: boolean;
}

const ProjectItemCard: React.FC<ProjectItemCardProps> = ({
  item,
  isSelected,
  onSelectChange,
  onNavigate,
  formatDate,
  selectable = true,
  variant = 'internal',
  onMenuAction,
  editingItemId = null,
  onRenameSubmit,
  onRenameCancel,
  entryMoveDnd = null,
  allowMoveToAndDuplicate = true,
  downloadProgress,
  isMoveDragSource = false,
  moveDragMode = null,
  canMergeToFile = true,
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editValue, setEditValue] = useState(item.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditing = editingItemId === item.id;
  const projectOrganizationLine = isEntryProject(item) ? getEntryOrganizationLabel(item) : '';
  const isFolderItem = item.entryType === 'Folder' || String(item.entryType) === 'Folder';
  const isFileItem = item.entryType === 'File' || String(item.entryType) === 'File' || item.entryType === 2;
  const canDragMove = Boolean(entryMoveDnd) && (isFolderItem || isFileItem) && !isEditing;

  const handleMoveDragStart = (e: React.DragEvent) => {
    if (!entryMoveDnd || isEditing) return;
    const t = e.target as HTMLElement;
    if (t.closest('button') || t.closest('input') || t.closest('.ant-checkbox') || t.closest(`.${styles.itemCardNameWrap}`)) {
      e.preventDefault();
      return;
    }
    entryMoveDnd.onMoveDragStart(e, item);
  };

  useEffect(() => {
    if (isEditing) {
      setEditValue(item.name);
      inputRef.current?.focus();
      const t = setTimeout(() => {
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isEditing, item.name]);

  const handleBlurOrSubmit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== item.name) {
      onRenameSubmit?.(item, trimmed);
    } else {
      onRenameCancel?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlurOrSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel?.();
    }
  };

  const handleMenuClick = (action: ProjectItemCardMenuAction) => {
    setMenuOpen(false);
    onMenuAction?.(action, item);
  };

  const menuItems: MenuProps['items'] = useMemo(() => {
    const role = user?.role;
    const canEdit = canEditEntryInCardMenu(role, item.entryType);
    const canDelete = canShowDeleteInCardMenu(role);
    const canDownload = canShowDownloadInCardMenu(!!user);

    const firstBlock: NonNullable<MenuProps['items']> = [];
    if (canEdit) {
      firstBlock.push(
        {
          key: 'rename',
          label: 'Rename',
          icon: (
            <span
              className={styles.itemCardMenuSvgIcon}
              style={{
                WebkitMaskImage: 'url(/images/icon/rename.svg)',
                maskImage: 'url(/images/icon/rename.svg)',
              }}
              aria-hidden
            />
          ),
        },
        {
          key: 'changeCover',
          label: 'Change Cover',
          icon: <EditOutlined style={{ fontSize: ICON_SIZE }} />,
        }
      );
    }
    if (allowMoveToAndDuplicate) {
      firstBlock.push(
        {
          key: 'moveTo',
          label: 'Move to',
          icon: (
            <span
              className={styles.itemCardMenuSvgIcon}
              style={{
                WebkitMaskImage: 'url(/images/icon/moveto.svg)',
                maskImage: 'url(/images/icon/moveto.svg)',
              }}
              aria-hidden
            />
          ),
        },
        {
          key: 'duplicate',
          label: 'Duplicate',
          icon: <CopyOutlined style={{ fontSize: ICON_SIZE }} />,
        }
      );
    }

    const restBlock: NonNullable<MenuProps['items']> = [];
    if (canEdit) {
      restBlock.push({
        key: 'share',
        label: 'Assign',
        icon: <ShareAltOutlined style={{ fontSize: ICON_SIZE }} />,
      });
    }
    if (canDownload) {
      restBlock.push({
        key: 'download',
        label: 'Download',
        icon: <DownloadOutlined style={{ fontSize: ICON_SIZE }} />,
      });
    }
    if (canDelete) {
      restBlock.push({
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined style={{ fontSize: ICON_SIZE }} />,
        danger: true,
      });
    }

    const combined: NonNullable<MenuProps['items']> = [...firstBlock];
    if (firstBlock.length > 0 && restBlock.length > 0) {
      combined.push({ type: 'divider' });
    }
    combined.push(...restBlock);
    return normalizeCardMenuDividers(combined);
  }, [user?.role, item.entryType, allowMoveToAndDuplicate]);

  const hasCardMenuItems = useMemo(
    () =>
      (menuItems || []).some(
        (it) =>
          !!it &&
          typeof it === 'object' &&
          (!('type' in it) || (it as { type?: string }).type !== 'divider')
      ),
    [menuItems]
  );

  const renderFilePreview = () => {
    if (item.entryType !== 'File') {
      return item.thumbnailUrl ? 
        <img src={item.thumbnailUrl} alt={item.name} /> : 
        <ImagePlaceholder className={styles.fallbackPlaceholder} />;
    }

    // File preview logic
    const thumbnailUrl = item.thumbnailUrl;
    const fileUrl = item.fileUrl || item.versions?.[0]?.fileUrl;
    
    // Eğer thumbnail varsa, direkt göster
    if (thumbnailUrl) {
      return <img src={thumbnailUrl} alt={item.name} className={styles.previewImage} />;
    }
    
    // Thumbnail yoksa, file URL ve dosya adından preview oluştur
    if (fileUrl) {
      let extension = '';
      const urlParts = fileUrl.split('/');
      const fileNameFromUrl = urlParts[urlParts.length - 1];
      if (fileNameFromUrl) {
        const fileNameParts = fileNameFromUrl.split('.');
        if (fileNameParts.length > 1) {
          extension = fileNameParts[fileNameParts.length - 1]?.split('?')[0]?.toLowerCase() ?? '';
        }
      }
      if (!extension && item.name && String(item.name).includes('.')) {
        const nameParts = String(item.name).split('.');
        if (nameParts.length > 1) {
          extension = nameParts[nameParts.length - 1]?.toLowerCase() ?? '';
        }
      }

      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension)) {
        return <img src={fileUrl} alt={item.name} className={styles.previewImage} />;
      }
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        return (
          <video
            src={fileUrl}
            className={styles.previewVideo}
            muted
            playsInline
            preload="metadata"
            onLoadedData={(e) => {
              const video = e.target as HTMLVideoElement;
              video.currentTime = 1;
            }}
          />
        );
      }
    }
    
    // Hiçbiri yoksa fallback
    return <ImagePlaceholder className={styles.fallbackPlaceholder} />;
  };

  const dragWrapClass = `${styles.contextMenuTrigger}${
    isEditing ? ` ${styles.moveDragDisabledWhileEditing}` : ''
  }${
    isMoveDragSource ? ` ${styles.moveDragSource}` : ''
  }${
    moveDragMode && !isMoveDragSource ? ` ${styles.moveDragGridActive}` : ''
  }${
    entryMoveDnd && isFolderItem && entryMoveDnd.isActiveFolderDropTarget ? ` ${styles.moveDropTarget}` : ''
  }${
    entryMoveDnd && isFileItem && entryMoveDnd.isActiveFileDropTarget ? ` ${styles.versionDropTarget}` : ''
  }${
    moveDragMode === 'file' && canMergeToFile && isFileItem && !isMoveDragSource && !entryMoveDnd?.isActiveFileDropTarget
      ? ` ${styles.versionDropCandidate}`
      : ''
  }${
    moveDragMode && isFolderItem && !isMoveDragSource && !entryMoveDnd?.isActiveFolderDropTarget
      ? ` ${styles.moveDropCandidate}`
      : ''
  }`;

  const showVersionDropOverlay = Boolean(entryMoveDnd?.isActiveFileDropTarget);
  const showFolderDropOverlay = Boolean(entryMoveDnd?.isActiveFolderDropTarget);

  const dragHandlers = {
    draggable: canDragMove,
    onDragStart: canDragMove ? handleMoveDragStart : undefined,
    onDragEnd: entryMoveDnd ? (ev: React.DragEvent) => entryMoveDnd.onMoveDragEnd(ev) : undefined,
    onDragOver:
      entryMoveDnd && isFolderItem
        ? (ev: React.DragEvent) => entryMoveDnd.onMoveFolderDragOver(ev, item)
        : entryMoveDnd && isFileItem
          ? (ev: React.DragEvent) => entryMoveDnd.onMoveFileDragOver(ev, item)
          : undefined,
    onDragLeave:
      entryMoveDnd && isFolderItem
        ? (ev: React.DragEvent) => entryMoveDnd.onMoveFolderDragLeave(ev, item)
        : entryMoveDnd && isFileItem
          ? (ev: React.DragEvent) => entryMoveDnd.onMoveFileDragLeave(ev, item)
          : undefined,
    onDrop:
      entryMoveDnd && isFolderItem
        ? (ev: React.DragEvent) => entryMoveDnd.onMoveFolderDrop(ev, item)
        : entryMoveDnd && isFileItem
          ? (ev: React.DragEvent) => entryMoveDnd.onMoveFileDrop(ev, item)
          : undefined,
  };

  const folderCardBlock = (
    <FolderCard
        key={item.id}
        selectable={selectable}
        selected={isSelected}
        onSelectChange={onSelectChange}
        folderType={item.entryType === 'File' ? 'file' : 'folder'}
        variant={variant}
        style={{ margin: 0, cursor: isEditing ? 'text' : canDragMove ? 'grab' : 'pointer' }}
        onClick={() => onNavigate(item)}
        sharedUsers={filterActiveShareMembers(item.share).map((shareItem: any) => ({
          id: shareItem.id || Math.random(),
          firstName: shareItem.firstName || shareItem.email?.split('@')[0] || 'User',
          lastName: shareItem.lastName || '',
          thumbnailUrl: shareItem.thumbnailUrl,
          organizationName: shareItem.organizationName,
          isDeleted: false,
        }))}

      >
      {/* 1. Görsel üstte - Folder'da margin-top, File'da yok; File ise sağ üst köşede status overlay */}
      <div className={`${styles.projectImageWrap} ${item.entryType === 'File' ? styles.projectImageWrapFile : styles.projectImageWrapFolder}`}>
        <div className={`${styles.projectImage} ${variant === 'external' ? styles.projectImageExternal : styles.projectImageInternal}`}>
          {renderFilePreview()}
        </div>
        {item.entryType === 'File' && (
          <div className={styles.fileStatusOverlay}>
            <div className={styles.projectStatus} style={{ backgroundColor: getStatusColor(item.versions?.[0]?.status || item.status) }}>
              {getStatusLabel(item.versions?.[0]?.status || item.status)}
            </div>
          </div>
        )}
      </div>

      {/* 2. Altında: üstte başlık; altta tarih + ⋯ (file: cardContentBelowFile) */}
      <div className={`${styles.cardContentBelow} ${item.entryType === 'File' || String(item.entryType) === 'File' ? styles.cardContentBelowFile : ''}`}>
        <div className={styles.cardContentTopBlock}>
          {isEditing ? (
              <div
                className={`${styles.itemCardNameWrap}${projectOrganizationLine ? ` ${styles.itemCardNameWrapWithOrg}` : ''}`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onDragStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  className={styles.itemCardNameInput}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleBlurOrSubmit}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  aria-label="Item name"
                />
              </div>
            ) : (
              <div
                className={styles.titleBlock}
                onDoubleClick={
                  DOUBLE_CLICK_RENAME_ENABLED
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMenuAction?.('rename', item);
                      }
                    : undefined
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                title={DOUBLE_CLICK_RENAME_ENABLED ? 'Double-click to rename' : undefined}
              >
                <span className={styles.titleText}>{item.name}</span>
                {projectOrganizationLine ? (
                  <span className={styles.organizationText}>{projectOrganizationLine}</span>
                ) : null}
              </div>
            )}
        </div>
        <div className={styles.cardContentBottomBar}>
          <div className={styles.cardMetaRow}>
              {item.childrenEntryCount != null && item.childrenEntryCount > 0 && (
                <span className={styles.childrenEntryCount}>
                  {item.entryType === 'File' || item.entryType === 2 || String(item.entryType) === 'File'
                    ? item.childrenEntryCount === 1
                      ? '1 version'
                      : `${item.childrenEntryCount} versions`
                    : item.childrenEntryCount === 1
                      ? '1 entry'
                      : `${item.childrenEntryCount} entries`}
                </span>
              )}
              <span className={styles.dateText}>
                {item.updatedAt ? formatDate(item.updatedAt) : formatDate(item.createdAt)}
              </span>
          </div>
          {hasCardMenuItems ? (
            <span className={styles.menuIconWrap} onClick={(e) => e.stopPropagation()}>
              <Dropdown
                open={menuOpen}
                onOpenChange={setMenuOpen}
                menu={{
                  items: menuItems,
                  className: `${styles.itemCardMenuOverlay} ${variant === 'external' ? styles.itemCardMenuOverlayExternal : ''}`,
                  onClick: ({ key }) => handleMenuClick(key as ProjectItemCardMenuAction),
                }}
                trigger={['click']}
                placement="bottomRight"
                getPopupContainer={() => document.body}
              >
                <button
                  type="button"
                  className={styles.itemCardMenuTrigger}
                  aria-label="Menu"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <EllipsisOutlined style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
                </button>
              </Dropdown>
            </span>
          ) : null}
        </div>
      </div>
    </FolderCard>
  );

  const cardWithOverlay = (
    <div className={styles.cardWithOverlay}>
      {folderCardBlock}
      {showVersionDropOverlay ? (
        <div className={`${styles.moveDropOverlay} ${styles.versionDropOverlay}`}>
          <span className={styles.moveDropBadge}>{t('projects.fileMergeActive')}</span>
        </div>
      ) : null}
      {showFolderDropOverlay ? (
        <div className={`${styles.moveDropOverlay} ${styles.folderDropOverlay}`}>
          <span className={styles.moveDropBadge}>{t('projects.folderDropActive')}</span>
        </div>
      ) : null}
      {downloadProgress != null ? <CardDownloadOverlay percent={downloadProgress} /> : null}
    </div>
  );

  return hasCardMenuItems ? (
    <Dropdown
      trigger={['contextMenu']}
      menu={{
        items: menuItems,
        className: `${styles.itemCardMenuOverlay} ${variant === 'external' ? styles.itemCardMenuOverlayExternal : ''}`,
        onClick: ({ key }) => handleMenuClick(key as ProjectItemCardMenuAction),
      }}
      getPopupContainer={() => document.body}
    >
      <div className={dragWrapClass} onContextMenu={(e) => e.preventDefault()} {...dragHandlers}>
        {cardWithOverlay}
      </div>
    </Dropdown>
  ) : (
    <div className={dragWrapClass} onContextMenu={(e) => e.preventDefault()} {...dragHandlers}>
      {cardWithOverlay}
    </div>
  );
};

export default ProjectItemCard;
